/* eslint-disable linebreak-style */

import axios from "axios";
import { AuthService } from "./auth";
import { getPlatform } from "@/hooks/usePlatform";
import { toast } from "@/utils/toastService";

const MAX_RETRIES = 0;
const RETRY_DELAY = 2000;

// set axios credetials to true
axios.defaults.withCredentials = true;

axios.interceptors.response.use(undefined, (err) => {
  const { config, response } = err;
  const retryCount = config.retryCount || 0;

  // If the response status is 5xx, retry the request
  if (response && response.status >= 500 && retryCount < MAX_RETRIES) {
    config.retryCount = retryCount + 1;

    return new Promise((resolve) =>
      setTimeout(() => resolve(axios(config)), RETRY_DELAY)
    );
  }

  // Show toast notification for errors
  const errorMessage =
    response?.data?.message ||
    response?.statusText ||
    err.message ||
    "An unexpected error occurred";

  // Skip toast for 401 errors (handled separately with redirect)
  // and for requests that opt out via skipErrorToast
  if (response?.status !== 401 && !config.skipErrorToast) {
    toast.error(errorMessage, {
      autoHideDuration: 5000,
    });
  }

  throw err;
});

let WORKSPACE = null;

export function getRelayDomain() {
  const { relayDomain } = getPlatform();

  return relayDomain;
}

export function getMonolithDomain() {
  const { monolithDomain } = getPlatform();

  return monolithDomain;
}

export async function setWorkspace(workspace) {
  WORKSPACE = workspace;
  axios.defaults.headers.common["x-tenant-slug"] = workspace;
}

export function getCurrentWorkspace() {
  return WORKSPACE;
}

export function getBaseURL() {
  const { apiUrl, monolithDomain } = getPlatform();

  axios.defaults.headers.common["x-monolith-api-host"] = apiUrl;
  axios.defaults.headers.common["x-monolith-origin"] = monolithDomain;

  return apiUrl;
}

export async function httpGet(api, params = {}, options = {}) {
  return axios
    .get(`${options.baseUrl || getBaseURL()}${api}`, {
      params,
      skipErrorToast: options.skipErrorToast,
    })
    .then(handleRepsonse)
    .catch(handleError);
}

export function httpGetURL(api, params = {}, options = {}) {
  // Create Get URL with query params
  let url = `${options.baseUrl || getBaseURL()}${api}`;

  if (params) {
    url += "?";
    Object.keys(params).forEach((key) => {
      url += `${key}=${params[key]}&`;
    });
  }

  return url;
}

export async function httpPost(api, body, options = {}) {
  const reqOptions = {
    method: "POST",
    url: `${getBaseURL()}${api}`,
    headers: {
      "content-type": "application/json",
    },
    data: JSON.stringify(body),
    onUploadProgress: options.onUploadProgress,
    skipErrorToast: options.skipErrorToast,
  };

  return axios(reqOptions).then(handleRepsonse).catch(handleError);
}

export async function httpPostFormData(api, formData, options = {}) {
  const reqOptions = {
    method: "POST",
    url: `${getBaseURL()}${api}`,
    headers: {
      "content-type": "multipart/form-data",
    },
    data: formData,
    onUploadProgress: options.onUploadProgress,
  };

  return axios(reqOptions)
    .then(handleRepsonse)
    .catch((res) => {
      if (res.response.status === 401) {
        window.location.reload(true);
      }
      return {
        status: res.response.status,
        success: false,
        message: res.response.data.message,
        formData: formData,
      };
    });
}

export async function httpPatch(api, body, options = {}) {
  const reqOptions = {
    method: "PATCH",
    url: `${getBaseURL()}${api}`,
    headers: {
      "content-type": "application/json",
    },
    data: JSON.stringify(body),
    skipErrorToast: options.skipErrorToast,
  };

  return axios(reqOptions).then(handleRepsonse).catch(handleError);
}

export async function httpDelete(api, params, options = {}) {
  return axios
    .delete(`${getBaseURL()}${api}`, {
      params,
      skipErrorToast: options.skipErrorToast,
    })
    .then(handleRepsonse)
    .catch(handleError);
}

export async function httpFileDownload(url, options = {}) {
  return axios
    .get(url, { responseType: "blob" })
    .then(handleRepsonse)
    .catch(handleError);
}

export async function httpKeepAlive() {
  return httpPost("/keep-alive", {});
}

const preventLogoutWhiteList = ["/check-session"];

const handleRepsonse = (response) => {
  if (response.status === 401) {
    console.log("401 unauthorized");
    // auto logout if 401 response returned from api
    AuthService.userLogout();
    window.location.reload(true);
  }

  return response.data;
};

const handleError = (error) => {
  console.error(error.config);

  if (
    error?.response?.status === 401 &&
    !preventLogoutWhiteList.includes(
      error.config.url.replace(error.config.headers["x-monolith-origin"], "")
    )
  ) {
    console.log("401 unauthorized");
    window.location.reload(true);
  }

  return { success: false, error };
};
